<template>
    <div class="provider-list mb-3 mt-page">

        <b-row>
            <b-col>
                <h1>{{ $i18n.gt('HEADING_MANAGE_PROVIDERS') }}</h1>
                <h3>{{ $i18n.gt('HEADING_FOR_PARTICIPANT') }}:
                    {{ participant ? participant.fullname : `#${participantId}` }}
                    {{ participant ? `(DOB: ${participant.dob})` : '' }}
                </h3>
            </b-col>
            <b-col class="text-right">
                <b-btn class="btn-ghost-blue"
                       @click="showModal()">
                    <shs-icon name="plus"></shs-icon>
                    {{ $i18n.gt('LABEL_NEW_PROVIDER') }}
                </b-btn>
            </b-col>
        </b-row>

        <!-- provider list -->
        <data-table ref="dataTable"
                    :get-items="getProviders"
                    :fields="fields">

            <!-- template for custom columns -->
            <template v-slot:customField="slotProps">
                <div v-if="slotProps.row.field.key == 'actions'">
                    <b-btn size="sm"
                           variant="danger"
                           @click="deleteProvider(slotProps.row.item)">
                        <shs-icon name="delete"></shs-icon>
                    </b-btn>
                </div>
            </template>

        </data-table>

        <!-- add provider modal -->
        <b-modal hide-footer
                 no-close-on-backdrop
                 body-class="light-bg"
                 title="Add Provider"
                 id="add-provider-modal">

            <b-form-group label="Practice">
                <vue-bootstrap-typeahead :data="practices"
                                         v-model="practiceSearch"
                                         placeholder="Search by practice name"
                                         :serializer="p => p.name"
                                         @hit="addProviderPractice = $event">
                </vue-bootstrap-typeahead>
            </b-form-group>

            <b-form-group label="Provider Type">
                <b-form-select :options="typeOptions"
                               v-model="addProviderType">
                </b-form-select>
            </b-form-group>

            <b-row>
                <b-col>
                    <b-form-checkbox v-model="cloneParticipant">
                        {{ $i18n.gt('LABEL_CLONE_PARTICIPANT') }}
                    </b-form-checkbox>
                </b-col>
                <b-col class="text-right">
                    <b-btn class="btn-primary"
                           :disabled="!canAddProvider"
                           @click="addProvider()">
                        {{ $i18n.gt('LABEL_ADD') }}
                    </b-btn>
                </b-col>
            </b-row>
        </b-modal>

    </div>
</template>

<!-- ----------------------------------------------------------------------- -->

<script>
    import Vue from 'vue'
    import VueBootstrapTypeahead from 'vue-bootstrap-typeahead'
    import * as _ from 'lodash'

    /**
     * Page for admin to manage Provider links for a selected Participant
     * (to determine which Practices can see the Participant)
     */
    export default Vue.extend({
        name: 'provider-list',
        components: {
            VueBootstrapTypeahead
        },
        props: {
            participantId: Number,
            resellerId: Number,
            companyId: Number
        },
        data () {
            return {
                participant: null,
                cloneParticipant: false,

                filters: {
                    participant_id: { field: 'participant_id', type: 'hidden', value: this.participantId },
                },
                fields: [
                    {
                        key: 'practice.name', sortable: true, label: 'Practice',
                        get: (item) => { return item.practice.name }
                    },
                    { key: 'type', sortable: true, label: 'Provider Type' },
                    { key: 'actions', sortable: false },
                ],

                // for typeahead
                practices: [],
                practiceSearch: '',
                addProviderPractice: {},

                addProviderType: null,
                typeOptions: [
                    { value: 'PRIMARY', text: 'Primary Care' },
                    { value: 'COACH', text: 'Health Coach' },
                    { value: 'MENTOR', text: 'Health Mentor' },
                ]
            }
        },
        computed: {
            canAddProvider () {
                return this.addProviderPractice.id != null &&
                    this.addProviderType != null
            }
        },
        methods: {
            showModal () {
                this.$bvModal.show('add-provider-modal')
            },
            getProviders (listParams) {
                return this.$api.getProviders(this, this.filters, listParams)
            },
            async deleteProvider (provider) {
                if (await this.$bvModal.msgBoxConfirm(`Remove provider ${provider.practice.name} from this Participant?`)) {
                    try {
                        await this.$api.deleteItem(this, 'provider', provider)
                        this.$toasted.show('Provider removed', { duration: 3000 })
                        this.$refs.dataTable.refresh()
                    } catch (e) {
                        this.$utils.handleError(e, 'Error removing provider')
                    }
                }
            },
            async getPractices (query) {
                const otherFilters = {}
                if (this.companyId) {
                    otherFilters.company_id = { field: 'company_id', value: this.companyId, type: 'hidden' }
                } else if (this.resellerId) {
                    otherFilters.reseller_id = { field: 'reseller_id', value: this.resellerId, type: 'hidden' }
                }
                this.$api.findPractices(this, query, otherFilters)
                .then((items) => {
                    this.practices = items
                })
            },
            addProvider () {
                this.$bvModal.hide('add-provider-modal')
                const provider = {
                    participant_id: this.participantId,
                    practice_id: this.addProviderPractice.id,
                    type: this.addProviderType
                }
                if (this.cloneParticipant) {
                    // NB: this is a temporary hack for demo purposes!!
                    this.$api.cloneParticipant(this, provider)
                        .then((response) => {
                            this.$toasted.show('Participant cloned', { duration: 3000 })
                            this.$refs.dataTable.refresh()
                        })
                } else {
                    this.$api.createItem(this, 'provider', provider)
                        .then((response) => {
                            this.$toasted.show('Provider added', { duration: 3000 })
                            this.$refs.dataTable.refresh()
                        })
                }
            }
        },
        mounted () {
            this.$api.getItem(this, 'participant', this.participantId)
                .then((item) => {
                    this.participant = item
                })
        },
        watch: {
            practiceSearch: _.debounce(function (name) {
                if(name) {
                    this.getPractices(name)
                }
            }, 500)
        }

    })

</script>
